import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper, Tabs, Tab } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, PartnerPermissionChange } from '../utils/transactionStore';
const FlagChip: React.FC<{
  value: boolean;
}> = ({
  value
}) => <Chip label={value ? 'Y' : 'N'} size="small" sx={{
  bgcolor: value ? 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.12)',
  color: value ? '#059669' : '#6B7280',
  fontWeight: 700,
  fontSize: '0.72rem',
  minWidth: 32
}} data-id="element-1515" data-mp="1m30z4maypy9p" />;
const MakerPartnerPermissionTable: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pending, setPending] = useState<PartnerPermissionChange[]>([]);
  const [rejected, setRejected] = useState<PartnerPermissionChange[]>([]);
  const [selected, setSelected] = useState<PartnerPermissionChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'resubmit' | 'discard' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => {
    setPending(transactionStore.getPendingPartnerPermissionChanges());
    setRejected(transactionStore.getRejectedPartnerPermissionChanges());
  };
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: PartnerPermissionChange, action: 'resubmit' | 'discard') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'resubmit') {
        transactionStore.resubmitPartnerPermissionChange(selected.id, reason);
        showToast(`Partner permission change resubmitted for "${selected.partnerName}"`, 'success');
      } else {
        transactionStore.discardPartnerPermissionChange(selected.id, reason);
        showToast(`Partner permission change discarded for "${selected.partnerName}"`, 'info');
      }
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const pendingColumns: Column<PartnerPermissionChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1516" data-mp="28xrq8y7zu51n">{row.id}</Typography>
  }, {
    id: 'partnerName',
    label: 'Partner',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 220
    }} data-id="element-1517" data-mp="3fdvf1hdwizqs">{row.partnerName}</Typography>
  }, {
    id: 'txnType',
    label: 'Txn Type',
    renderCell: row => <Chip label={row.txnType} size="small" sx={{
      fontWeight: 700
    }} data-id="element-1518" data-mp="18oteoob1k09e" />
  }, {
    id: 'change',
    label: 'Change',
    renderCell: row => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75
    }} data-id="element-1519" data-mp="13rjqtd3avwno"><FlagChip value={row.currentAllowed} data-id="element-1520" data-mp="yz9t3avy1sau" /><Icon icon="lucide:arrow-right" width={13} color="#9CA3AF" data-id="element-1521" data-mp="yy8jzbyhnfvr" /><FlagChip value={row.newAllowed} data-id="element-1522" data-mp="u1qg0eazflkh" /></Box>
  }, {
    id: 'effectiveDate',
    label: 'Effective Date',
    renderCell: row => <Chip label={row.effectiveDate} size="small" sx={{
      fontWeight: 700,
      fontSize: '0.7rem',
      bgcolor: 'rgba(91,78,240,0.1)',
      color: '#5B4EF0'
    }} data-id="element-1523" data-mp="q04uk4kdtyyo" />
  }, {
    id: 'status',
    label: 'Status',
    renderCell: () => <Chip label="PENDING" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1524" data-mp="g1yxc09ith05" />
  }, {
    id: 'requestedAt',
    label: 'Submitted At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1525" data-mp="2aef9tiswtrdg">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  const rejectedColumns: Column<PartnerPermissionChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1526" data-mp="2ielcgcrhpwvu">{row.id}</Typography>
  }, {
    id: 'partnerName',
    label: 'Partner',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      maxWidth: 200
    }} data-id="element-1527" data-mp="28h7655za6oh9">{row.partnerName}</Typography>
  }, {
    id: 'txnType',
    label: 'Txn Type',
    renderCell: row => <Chip label={row.txnType} size="small" sx={{
      fontWeight: 700
    }} data-id="element-1528" data-mp="1uoh6vzuqrn27" />
  }, {
    id: 'change',
    label: 'Change',
    renderCell: row => <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75
    }} data-id="element-1529" data-mp="1kse36pk8rgd5"><FlagChip value={row.currentAllowed} data-id="element-1530" data-mp="1kq1ezunw49rn" /><Icon icon="lucide:arrow-right" width={13} color="#9CA3AF" data-id="element-1531" data-mp="1pqpg595u26zl" /><FlagChip value={row.newAllowed} data-id="element-1532" data-mp="1pp4kthbp2k0o" /></Box>
  }, {
    id: 'checkerComment',
    label: 'Rejection Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" color="error.main" noWrap sx={{
      maxWidth: 150
    }} data-id="element-1533" data-mp="1ft1jueskwrfb">{row.checkerComment}</Typography>
  }, {
    id: 'checkerActedAt',
    label: 'Rejected At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1534" data-mp="s5nxqtuvb5kf">{new Date(row.checkerActedAt || '').toLocaleString()}</Typography>
  }];
  const rejectedRowActions: RowAction<PartnerPermissionChange>[] = [{
    icon: 'lucide:rotate-cw',
    label: 'Resubmit',
    onClick: row => handleAction(row, 'resubmit')
  }, {
    icon: 'lucide:trash-2',
    label: 'Delete',
    onClick: row => handleAction(row, 'discard')
  }];
  return <Box data-id="element-1535" data-mp="1b3h11x9elhic">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-1536" data-mp="1b3qrtn29vsp3">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} data-id="element-1537" data-mp="1owgd1p9z8tl5">
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1539" data-mp="1kule2rfiqsaj"><Typography variant="body2" fontWeight={500} data-id="element-1540" data-mp="1kv4x6bgwnmq5">Pending</Typography><Chip label={pending.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            color: '#F59E0B'
          }} data-id="element-1541" data-mp="1ytbd4clwf21j" /></Box>} data-id="element-1538" data-mp="1oxrkbz2msjh0" />
          <Tab label={<Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1543" data-mp="rhvht5kfoye5"><Typography variant="body2" fontWeight={500} data-id="element-1544" data-mp="rjg9mnfd638b">Rejected</Typography><Chip label={rejected.length} size="small" sx={{
            height: 20,
            fontSize: '0.7rem',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#EF4444'
          }} data-id="element-1545" data-mp="vk8l7nbpkk7r" /></Box>} data-id="element-1542" data-mp="rhlphbbx2f5a" />
        </Tabs>
      </Box>

      {activeTab === 0 ? <DataTable title="Pending Partner Permission Changes" data={pending} columns={pendingColumns} keyField="id" emptyStateMessage="No pending partner permission changes" initialVisibleColumns={['partnerName', 'txnType', 'change', 'effectiveDate', 'status', 'requestedAt']} data-id="element-1546" data-mp="159s1qmby81zg" /> : <DataTable title="Rejected Partner Permission Changes" data={rejected} columns={rejectedColumns} rowActions={rejectedRowActions} keyField="id" emptyStateMessage="No rejected partner permission changes" initialVisibleColumns={['partnerName', 'txnType', 'change', 'checkerComment', 'checkerActedAt']} data-id="element-1547" data-mp="1j8jcpzvil7jd" />}

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1548" data-mp="3q00r3uzz39w5">
        <DialogTitle data-id="element-1549" data-mp="3ih183qgfbkdb">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-1550" data-mp="3dozzceigsbs1">
            <Icon icon={actionType === 'resubmit' ? 'lucide:rotate-cw' : 'lucide:trash-2'} width={24} color={actionType === 'resubmit' ? '#0EA5E9' : '#EF4444'} data-id="element-1551" data-mp="3dnox4j4o3c31" />
            <Typography variant="h6" fontWeight={600} data-id="element-1552" data-mp="33rlkgjc6vv1u">{actionType === 'resubmit' ? 'Resubmit' : 'Delete'} Partner Permission Change</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-1553" data-mp="ad649go7dbv">
          <Box sx={{
          mt: 1
        }} data-id="element-1554" data-mp="b51bj6cv91m">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-1555" data-mp="3rfvbbzo9f8zu">
                  Partner: <strong data-id="element-1556" data-mp="3nf2zqzrx0s0e">{selected.partnerName}</strong> &middot; Txn Type: <strong data-id="element-1557" data-mp="3nejk5ppqmfpj">{selected.txnType}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-1558" data-mp="2g20xlpcx0dna">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-1559" data-mp="2byl2wykeond8">Original Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-1560" data-mp="ey8fjp5hnijk">{selected.resubmitReason || selected.makerReason}</Typography>
                  <Typography variant="caption" color="#EF4444" fontWeight={600} data-id="element-1561" data-mp="jvqjt28k99l8">Checker's Rejection Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-1562" data-mp="ax52faets0m1">{selected.checkerComment}</Typography>
                </Paper>
              </>}
            <TextField label={`Reason for ${actionType === 'resubmit' ? 'Resubmission' : 'Deleting'}`} placeholder={`Enter reason for ${actionType === 'resubmit' ? 'resubmitting' : 'deleting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-1563" data-mp="yniv9358i3ho" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1564" data-mp="1hllk9hxs80fh">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-1565" data-mp="1hjs9bqg4yu1o">Cancel</Button>
          <Button variant="contained" color={actionType === 'resubmit' ? 'primary' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-1567" data-mp="1rpabjkg30k2i" /> : <Icon icon={actionType === 'resubmit' ? 'lucide:send' : 'lucide:trash'} width={16} data-id="element-1568" data-mp="25h6wn4jc1tpe" />} data-id="element-1566" data-mp="1meoezteapfx9">
            {actionType === 'resubmit' ? 'Resubmit' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default MakerPartnerPermissionTable;